import React from 'react';

import {
  Box,
  Flex,
  Text,
  PresentationalCheckboxGroup,
  PresentationalRadioGroup,
} from '@opendoor/bricks/core';
import { MarginProps } from '@opendoor/bricks/system/theme';
import { GroupOption } from '@opendoor/bricks/core/Input';

interface MultipleChoiceQuestionProps {
  header?: string;
  /** The key/id for this question. */
  questionKey: string;
  /** The text representing the actual question */
  question: string;
  /** Optional description to display additional information */
  description?: string;
  /** The available answers to this question. */
  answers: Array<GroupOption>;
  /** Callback for when the user selects an answer. */
  onSelection: (value: string) => void;
  /** Disables selection if true */
  disabled?: boolean;
  /** Indicates the type of form to display for InputGroup */
  formType: 'radio' | 'checkbox';
  /** A single value for selecting a radio input */
  selectedValue?: string | undefined;
  /** An array of values that can be use to have selections already checked */
  selectedValues?: string[] | undefined;
}

/**
 * Component to render a multiple choice question.
 *
 * component-test-check-ignore
 */
export const MultipleChoiceQuestion: React.FC<MultipleChoiceQuestionProps & MarginProps> = ({
  header,
  question,
  description,
  answers,
  onSelection,
  disabled,
  questionKey,
  formType,
  selectedValue,
  selectedValues,
  ...marginProps
}) => (
  <Flex
    flexDir="column"
    alignItems="flex-start"
    width="100%"
    gridGap={[7, 7, 8]}
    mt={[0, 0, 8]}
    {...marginProps}
  >
    <Flex flexDirection="column">
      {header && (
        <Text textAlign="center" fontSize="s2" fontWeight="semibold" color="neutrals90" mb={5}>
          {header}
        </Text>
      )}
      <Text
        as="label"
        fontSize={['s3', null, 's4']}
        lineHeight={['s3', null, 's4']}
        fontWeight="bold"
        mb="7"
        textAlign={['start', null, 'center']}
        id={question}
      >
        {question}
      </Text>
      {description && <Text textAlign={['start', null, 'center']}>{description}</Text>}
    </Flex>
    <Box width="100%">
      {formType === 'radio' && (
        <PresentationalRadioGroup
          analyticsName={`cake-question-flow-${questionKey}`}
          name={questionKey}
          onClick={onSelection}
          options={answers}
          disabled={disabled}
          hasContainer
          selectedValue={selectedValue}
          aria-labelledby={question}
        />
      )}
      {formType === 'checkbox' && (
        <PresentationalCheckboxGroup
          analyticsName={`cake-question-flow-${questionKey}`}
          name={questionKey}
          onClick={onSelection}
          options={answers}
          disabled={disabled}
          hasContainer
          selectedValues={selectedValues}
          aria-labelledby={question}
        />
      )}
    </Box>
  </Flex>
);
